// HR Recruitment Coordinator screens — Dashboard, Shortlist, Interview, Report.
// All written with React.createElement to keep Babel happy on big tables/forms.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Users, FileSpreadsheet, ListChecks, ClipboardCheck, Calendar, FileDown,
  CheckCircle2, XCircle, ChevronRight, Plus, Trash2, Upload, ScanFace, BarChart3,
  Award, AlertTriangle, Briefcase, FilePlus, Mail, Phone, GraduationCap
} from "lucide-react";

const h = React.createElement;
const API = process.env.REACT_APP_BACKEND_URL + "/api/hr";

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "\u2014";
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 16);
};
const authHeader = () => {
  const tok = localStorage.getItem("token");
  return tok ? { Authorization: "Bearer " + tok } : {};
};

// ============================================================================
// HR Dashboard — 5-tile landing
// ============================================================================
export function HRDashboard() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    axios.get(API + "/sessions", { headers: authHeader() })
      .then(r => setSessions(r.data.items || []))
      .catch(() => setSessions([]));
  }, []);

  const tiles = [
    { to: "/exam-entry/manage", icon: FileSpreadsheet, title: "Manage Database", desc: "Create sessions, upload Excel rosters & candidate photos.", color: "amber" },
    { to: "/exam-entry/face-verify", icon: ScanFace, title: "Examination Verification", desc: "Live face-recognition kiosk for hall entry on exam day.", color: "emerald" },
    { to: "/hr/shortlist", icon: ListChecks, title: "Shortlist Candidates", desc: "Capture exam scores, remarks & mark candidates for interview.", color: "blue" },
    { to: "/hr/interview", icon: ClipboardCheck, title: "Interview Schedule", desc: "Define criteria, schedule slots, capture panel scores.", color: "indigo" },
    { to: "/hr/report", icon: FileDown, title: "Final Report", desc: "Rank candidates and download branded selection PDFs.", color: "rose" },
  ];
  const colorMap = {
    amber: "from-amber-50 to-white border-amber-200 hover:border-amber-400 text-amber-700",
    emerald: "from-emerald-50 to-white border-emerald-200 hover:border-emerald-400 text-emerald-700",
    blue: "from-blue-50 to-white border-blue-200 hover:border-blue-400 text-blue-700",
    indigo: "from-indigo-50 to-white border-indigo-200 hover:border-indigo-400 text-indigo-700",
    rose: "from-rose-50 to-white border-rose-200 hover:border-rose-400 text-rose-700",
  };

  return h("div", { className: "space-y-6" },
    h("div", null,
      h("h1", { className: "text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2" },
        h(Briefcase, { className: "w-7 h-7 text-amber-500" }), "HR Recruitment Coordinator"
      ),
      h("p", { className: "text-sm text-slate-600 mt-1" },
        "Welcome, ", h("b", null, user?.name || "Coordinator"), ". End-to-end pipeline for institute recruitment exams."
      )
    ),
    h("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" },
      tiles.map((t, i) => h(Link, {
        key: i, to: t.to,
        className: "block p-5 rounded-2xl border-2 bg-gradient-to-br transition-all hover:shadow-lg " + colorMap[t.color],
        "data-testid": "hr-tile-" + i
      },
        h("div", { className: "flex items-start justify-between" },
          h("div", { className: "w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center" },
            h(t.icon, { className: "w-6 h-6" })
          ),
          h(ChevronRight, { className: "w-5 h-5 opacity-50" })
        ),
        h("h3", { className: "mt-3 font-bold text-slate-900" }, t.title),
        h("p", { className: "text-xs text-slate-600 mt-1 leading-relaxed" }, t.desc)
      ))
    ),
    h(Card, null, h(CardContent, { className: "p-5" },
      h("h3", { className: "font-semibold text-slate-900 flex items-center gap-2 mb-3" },
        h(Calendar, { className: "w-4 h-4 text-amber-500" }), "Active & Recent Sessions"
      ),
      sessions.length === 0
        ? h("p", { className: "text-sm text-slate-500 italic" }, "No sessions yet. Start by creating one in ", h(Link, { to: "/exam-entry/manage", className: "text-amber-600 underline" }, "Manage Database"), ".")
        : h("div", { className: "overflow-x-auto" }, h("table", { className: "w-full text-sm" },
            h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
              h("th", { className: "px-3 py-2" }, "Session"),
              h("th", { className: "px-3 py-2" }, "Position"),
              h("th", { className: "px-3 py-2" }, "Window"),
              h("th", { className: "px-3 py-2 text-center" }, "Roster"),
              h("th", { className: "px-3 py-2 text-center" }, "Shortlisted"),
              h("th", { className: "px-3 py-2 text-center" }, "Interviewed"),
              h("th", { className: "px-3 py-2" }, "")
            )),
            h("tbody", { className: "divide-y divide-slate-100" }, sessions.map(s =>
              h("tr", { key: s.id, className: "hover:bg-slate-50", "data-testid": "hr-session-row-" + s.id },
                h("td", { className: "px-3 py-2 font-semibold" }, s.name),
                h("td", { className: "px-3 py-2 text-xs" }, s.position_name || h("span", { className: "text-slate-400 italic" }, "—")),
                h("td", { className: "px-3 py-2 text-xs text-slate-500" }, fmtDT(s.start_at) + " → " + fmtDT(s.end_at)),
                h("td", { className: "px-3 py-2 text-center text-xs font-mono" }, s.roster_count),
                h("td", { className: "px-3 py-2 text-center text-xs font-mono text-blue-700" }, s.shortlisted_count || 0),
                h("td", { className: "px-3 py-2 text-center text-xs font-mono text-indigo-700" }, s.interviewed_count || 0),
                h("td", { className: "px-3 py-2 text-right" },
                  h(Link, { to: "/hr/shortlist/" + s.id, className: "text-xs text-amber-700 hover:underline mr-3" }, "Shortlist"),
                  h(Link, { to: "/hr/interview/" + s.id, className: "text-xs text-indigo-700 hover:underline mr-3" }, "Interview"),
                  h(Link, { to: "/hr/report/" + s.id, className: "text-xs text-rose-700 hover:underline" }, "Report")
                )
              )
            ))
          ))
    ))
  );
}

// ============================================================================
// Session picker (re-usable) — list of HR-visible sessions
// ============================================================================
function HRSessionPicker({ basePath, title, subtitle, icon: Icon }) {
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    axios.get(API + "/sessions", { headers: authHeader() })
      .then(r => setSessions(r.data.items || []))
      .catch(() => setSessions([]));
  }, []);
  return h("div", { className: "space-y-4" },
    h("div", null,
      h("h1", { className: "text-2xl font-bold text-slate-900 flex items-center gap-2" },
        Icon && h(Icon, { className: "w-6 h-6 text-amber-500" }), title
      ),
      h("p", { className: "text-sm text-slate-600" }, subtitle)
    ),
    h(Card, null, h(CardContent, { className: "p-5" },
      sessions.length === 0
        ? h("p", { className: "text-sm text-slate-500 italic text-center py-10" },
            "No sessions to work on. Create one in ",
            h(Link, { to: "/exam-entry/manage", className: "text-amber-600 underline" }, "Manage Database"), "."
          )
        : h("div", { className: "space-y-2" },
            sessions.map(s => h(Link, {
              key: s.id, to: basePath + "/" + s.id,
              className: "block p-4 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50/30 transition-all",
              "data-testid": "pick-session-" + s.id
            },
              h("div", { className: "flex items-center justify-between gap-3" },
                h("div", { className: "min-w-0 flex-1" },
                  h("p", { className: "font-semibold text-slate-900" }, s.name,
                    s.position_name && h("span", { className: "ml-2 text-sm text-slate-500 font-normal" }, "· " + s.position_name)
                  ),
                  h("p", { className: "text-xs text-slate-500 mt-0.5" }, fmtDT(s.start_at) + " → " + fmtDT(s.end_at)),
                  h("div", { className: "flex items-center gap-3 mt-1.5 text-xs text-slate-600" },
                    h("span", null, h("b", null, s.roster_count), " roster"),
                    h("span", null, h("b", null, s.shortlisted_count || 0), " shortlisted"),
                    h("span", null, h("b", null, s.interviewed_count || 0), " interviewed")
                  )
                ),
                h(ChevronRight, { className: "w-5 h-5 text-slate-400" })
              )
            ))
          )
    ))
  );
}

// ============================================================================
// Shortlist Candidates — exam score entry + bulk upload + shortlist toggle
// ============================================================================
export function HRShortlist() {
  const sid = window.location.pathname.split("/hr/shortlist/")[1];
  if (!sid) return h(HRSessionPicker, { basePath: "/hr/shortlist", title: "Shortlist Candidates", subtitle: "Pick a session to enter exam scores and mark candidates for interview.", icon: ListChecks });
  return h(HRShortlistDetail, { sid });
}

function HRShortlistDetail({ sid }) {
  const [session, setSession] = useState(null);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null); // {application_no, exam_score, exam_max, exam_remarks, shortlisted}
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(API + "/sessions", { headers: authHeader() })
      .then(r => setSession((r.data.items || []).find(s => s.id === sid) || null));
    axios.get(API + "/sessions/" + sid + "/candidates", { headers: authHeader() })
      .then(r => setList(r.data.items || []))
      .catch(() => toast.error("Could not load candidates"));
  }, [sid]);
  useEffect(load, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter(c => {
      if (filter === "attended" && !c.attended) return false;
      if (filter === "shortlisted" && !c.shortlisted) return false;
      if (filter === "graded" && c.exam_score == null) return false;
      if (q && !(c.application_no.toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [list, search, filter]);

  const saveScore = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await axios.put(API + "/sessions/" + sid + "/exam-scores/" + edit.application_no, {
        exam_score: edit.exam_score,
        exam_max: edit.exam_max || 100,
        exam_remarks: edit.exam_remarks,
        shortlisted: !!edit.shortlisted,
      }, { headers: authHeader() });
      toast.success("Score saved");
      setEdit(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const toggleShortlist = async (c) => {
    try {
      await axios.put(API + "/sessions/" + sid + "/exam-scores/" + c.application_no, {
        exam_score: c.exam_score,
        exam_max: c.exam_max || 100,
        exam_remarks: c.exam_remarks || "",
        shortlisted: !c.shortlisted,
      }, { headers: authHeader() });
      load();
    } catch { toast.error("Could not toggle"); }
  };

  const uploadBulk = async () => {
    if (!bulkFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("excel_file", bulkFile);
      const r = await axios.post(API + "/sessions/" + sid + "/exam-scores/bulk", fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      toast.success(r.data.updated + " rows updated" + (r.data.skipped?.length ? ` (${r.data.skipped.length} skipped)` : ""));
      setBulkOpen(false); setBulkFile(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const stats = useMemo(() => ({
    total: list.length,
    attended: list.filter(c => c.attended).length,
    graded: list.filter(c => c.exam_score != null).length,
    shortlisted: list.filter(c => c.shortlisted).length,
  }), [list]);

  return h("div", { className: "space-y-4" },
    h("div", { className: "flex items-start justify-between flex-wrap gap-3" },
      h("div", null,
        h("h1", { className: "text-2xl font-bold text-slate-900 flex items-center gap-2" },
          h(ListChecks, { className: "w-6 h-6 text-amber-500" }), "Shortlist — ", session?.name || "…"
        ),
        session?.position_name && h("p", { className: "text-sm text-slate-600 mt-0.5" }, "Position: ", h("b", null, session.position_name))
      ),
      h("div", { className: "flex items-center gap-2" },
        h(Button, { variant: "outline", size: "sm", onClick: () => setBulkOpen(true), "data-testid": "bulk-upload-btn" },
          h(Upload, { className: "w-3 h-3 mr-1" }), "Bulk Upload Scores"
        ),
        h(Link, { to: "/hr/interview/" + sid, className: "inline-flex items-center gap-1 text-xs px-3 py-2 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100" },
          "Next: Interview ", h(ChevronRight, { className: "w-3 h-3" })
        )
      )
    ),
    h("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-3" },
      [
        { label: "In Roster", value: stats.total, color: "slate" },
        { label: "Attended Exam", value: stats.attended, color: "emerald" },
        { label: "Scores Recorded", value: stats.graded, color: "amber" },
        { label: "Shortlisted", value: stats.shortlisted, color: "blue" },
      ].map((s, i) => h("div", { key: i, className: "p-3 rounded-xl border bg-white" },
        h("p", { className: "text-[10px] uppercase tracking-wider text-slate-500 font-semibold" }, s.label),
        h("p", { className: "text-2xl font-bold text-slate-900 mt-0.5" }, s.value)
      ))
    ),
    h(Card, null, h(CardContent, { className: "p-4" },
      h("div", { className: "flex items-center gap-2 mb-3 flex-wrap" },
        h(Input, { placeholder: "Search by Application No or name…", value: search, onChange: e => setSearch(e.target.value), className: "max-w-sm" }),
        ["all", "attended", "graded", "shortlisted"].map(f =>
          h(Button, {
            key: f, variant: filter === f ? "default" : "outline", size: "sm",
            onClick: () => setFilter(f), "data-testid": "filter-" + f,
            className: filter === f ? "bg-slate-900" : ""
          }, f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1))
        )
      ),
      h("div", { className: "overflow-x-auto" }, h("table", { className: "w-full text-sm" },
        h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
          h("th", { className: "px-3 py-2" }, "App. No."),
          h("th", { className: "px-3 py-2" }, "Name"),
          h("th", { className: "px-3 py-2 text-center" }, "Attended"),
          h("th", { className: "px-3 py-2 text-center" }, "Score"),
          h("th", { className: "px-3 py-2" }, "Remarks"),
          h("th", { className: "px-3 py-2 text-center" }, "Shortlist"),
          h("th", { className: "px-3 py-2 text-right" }, "")
        )),
        h("tbody", { className: "divide-y divide-slate-100" }, filtered.map(c =>
          h("tr", { key: c.application_no, className: "hover:bg-slate-50", "data-testid": "cand-" + c.application_no },
            h("td", { className: "px-3 py-2 font-mono text-xs" }, c.application_no),
            h("td", { className: "px-3 py-2 font-medium" }, c.name),
            h("td", { className: "px-3 py-2 text-center" },
              c.attended ? h(CheckCircle2, { className: "w-4 h-4 text-emerald-500 inline" }) : h(XCircle, { className: "w-4 h-4 text-rose-300 inline" })
            ),
            h("td", { className: "px-3 py-2 text-center text-xs font-mono" },
              c.exam_score != null ? c.exam_score + " / " + (c.exam_max || 100) : h("span", { className: "text-slate-400 italic" }, "—")
            ),
            h("td", { className: "px-3 py-2 text-xs text-slate-600 truncate max-w-[200px]" }, c.exam_remarks || "—"),
            h("td", { className: "px-3 py-2 text-center" },
              h("input", { type: "checkbox", checked: !!c.shortlisted, onChange: () => toggleShortlist(c), "data-testid": "shortlist-" + c.application_no, className: "w-4 h-4 accent-amber-500 cursor-pointer" })
            ),
            h("td", { className: "px-3 py-2 text-right" },
              h(Button, {
                size: "sm", variant: "ghost",
                onClick: () => setEdit({
                  application_no: c.application_no, name: c.name,
                  exam_score: c.exam_score ?? "", exam_max: c.exam_max ?? 100,
                  exam_remarks: c.exam_remarks ?? "", shortlisted: !!c.shortlisted,
                }),
                "data-testid": "edit-" + c.application_no
              }, "Edit")
            )
          )
        ))
      ))
    )),

    // Edit dialog
    edit && h(Dialog, { open: true, onOpenChange: () => setEdit(null) },
      h(DialogContent, null,
        h(DialogHeader, null, h(DialogTitle, null, "Exam Score — " + edit.name + " (" + edit.application_no + ")")),
        h("div", { className: "space-y-3" },
          h("div", { className: "grid grid-cols-2 gap-3" },
            h("div", null,
              h(Label, null, "Score"),
              h(Input, { type: "number", step: "0.01", value: edit.exam_score, onChange: e => setEdit({ ...edit, exam_score: e.target.value }), "data-testid": "score-input" })
            ),
            h("div", null,
              h(Label, null, "Max Score"),
              h(Input, { type: "number", value: edit.exam_max, onChange: e => setEdit({ ...edit, exam_max: e.target.value }), "data-testid": "max-input" })
            )
          ),
          h("div", null,
            h(Label, null, "Remarks"),
            h(Textarea, { rows: 3, value: edit.exam_remarks, onChange: e => setEdit({ ...edit, exam_remarks: e.target.value }), "data-testid": "remarks-input" })
          ),
          h("label", { className: "flex items-center gap-2" },
            h("input", { type: "checkbox", checked: !!edit.shortlisted, onChange: e => setEdit({ ...edit, shortlisted: e.target.checked }), "data-testid": "shortlist-checkbox", className: "w-4 h-4 accent-amber-500" }),
            h("span", { className: "text-sm" }, "Shortlist this candidate for interview")
          )
        ),
        h(DialogFooter, { className: "mt-4" },
          h(Button, { variant: "outline", onClick: () => setEdit(null) }, "Cancel"),
          h(Button, { onClick: saveScore, disabled: busy, className: "bg-slate-900", "data-testid": "save-score-btn" }, busy ? "Saving…" : "Save")
        )
      )
    ),

    // Bulk upload dialog
    h(Dialog, { open: bulkOpen, onOpenChange: setBulkOpen },
      h(DialogContent, null,
        h(DialogHeader, null, h(DialogTitle, null, "Bulk Upload Exam Scores")),
        h("p", { className: "text-xs text-slate-600" },
          "Upload an Excel with columns: ",
          h("b", null, "Application No"), ", ", h("b", null, "Score"), ", optional ",
          h("code", null, "Max Score"), ", ", h("code", null, "Remarks"), ", ", h("code", null, "Shortlist (Y/N)"), "."
        ),
        h("input", {
          type: "file", accept: ".xlsx,.xls",
          onChange: e => setBulkFile(e.target.files?.[0] || null),
          "data-testid": "bulk-file-input",
          className: "block w-full text-sm border border-slate-300 rounded p-2 file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-slate-900 file:text-white"
        }),
        h(DialogFooter, { className: "mt-4" },
          h(Button, { variant: "outline", onClick: () => setBulkOpen(false) }, "Cancel"),
          h(Button, { onClick: uploadBulk, disabled: busy || !bulkFile, className: "bg-slate-900", "data-testid": "bulk-upload-confirm" }, busy ? "Uploading…" : "Upload")
        )
      )
    )
  );
}

// ============================================================================
// Interview Module — Configure (criteria + panelists) and Score Entry
// ============================================================================
export function HRInterview() {
  // Parse path: /hr/interview[/{sid}[/configure | /score[/{appNo}]]]
  const path = window.location.pathname;
  const m = path.match(/^\/hr\/interview(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?\/?$/);
  const sid = m?.[1];
  const section = m?.[2]; // "configure" | "score" | undefined
  const appNo = m?.[3];

  if (!sid) return h(HRSessionPicker, {
    basePath: "/hr/interview",
    title: "Interview Schedule",
    subtitle: "Pick a session to configure interview criteria & panelists, or capture scores for shortlisted candidates.",
    icon: ClipboardCheck,
  });
  if (section === "configure") return h(InterviewConfigure, { sid });
  if (section === "score" && appNo) return h(InterviewScorePage, { sid, appNo });
  if (section === "score") return h(InterviewScoreList, { sid });
  return h(InterviewLanding, { sid });
}

function InterviewLanding({ sid }) {
  const [session, setSession] = useState(null);
  const [counts, setCounts] = useState({ criteria: 0, panelists: 0, shortlisted: 0, scored: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(API + "/sessions", { headers: authHeader() })
      .then(r => setSession((r.data.items || []).find(s => s.id === sid))).catch(() => {});
    // Fire independently so one failure doesn't blank everything
    let local = { criteria: 0, panelists: 0, shortlisted: 0, scored: 0 };
    Promise.allSettled([
      axios.get(API + "/sessions/" + sid + "/criteria", { headers: authHeader() }),
      axios.get(API + "/sessions/" + sid + "/panelists", { headers: authHeader() }),
      axios.get(API + "/sessions/" + sid + "/candidates", { headers: authHeader() }),
      axios.get(API + "/sessions/" + sid + "/summary", { headers: authHeader() }),
    ]).then(([cr, p, c, s]) => {
      if (cr.status === "fulfilled") local.criteria = cr.value.data.count || 0;
      if (p.status === "fulfilled") local.panelists = p.value.data.count || 0;
      if (c.status === "fulfilled") local.shortlisted = (c.value.data.items || []).filter(x => x.shortlisted).length;
      if (s.status === "fulfilled") local.scored = (s.value.data.candidates || []).filter(x => x.interview_total_pct != null).length;
      setCounts(local);
    });
  }, [sid]);

  const ready = counts.criteria > 0 && counts.panelists > 0;
  return h("div", { className: "space-y-5" },
    h("div", null,
      h("h1", { className: "text-2xl font-bold text-slate-900 flex items-center gap-2" },
        h(ClipboardCheck, { className: "w-6 h-6 text-indigo-500" }), "Interview — ", session?.name || "…"
      ),
      session?.position_name && h("p", { className: "text-sm text-slate-600 mt-0.5" }, "Position: ", h("b", null, session.position_name))
    ),
    h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
      h("button", {
        onClick: () => navigate("/hr/interview/" + sid + "/configure"),
        className: "p-6 rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white hover:border-indigo-400 hover:shadow-lg transition-all text-left",
        "data-testid": "interview-configure-tile"
      },
        h("div", { className: "flex items-center justify-between" },
          h("div", { className: "w-12 h-12 rounded-xl bg-indigo-500 text-white flex items-center justify-center shadow" },
            h(ClipboardCheck, { className: "w-6 h-6" })
          ),
          h(ChevronRight, { className: "w-5 h-5 text-indigo-400" })
        ),
        h("h3", { className: "mt-3 font-bold text-slate-900 text-lg" }, "1. Configure"),
        h("p", { className: "text-xs text-slate-600 mt-1" }, "Set up the evaluation criteria and panel members before interviews begin."),
        h("div", { className: "mt-3 flex items-center gap-3 text-xs" },
          h("span", { className: "px-2 py-0.5 rounded-full bg-white border border-slate-200" }, h("b", null, counts.criteria), " criteria"),
          h("span", { className: "px-2 py-0.5 rounded-full bg-white border border-slate-200" }, h("b", null, counts.panelists), " panelists")
        )
      ),
      h("button", {
        onClick: () => ready ? navigate("/hr/interview/" + sid + "/score") : toast.error("Add at least one criterion and one panelist first"),
        className: "p-6 rounded-2xl border-2 transition-all text-left " + (ready ? "border-amber-200 bg-gradient-to-br from-amber-50 to-white hover:border-amber-400 hover:shadow-lg" : "border-slate-200 bg-slate-50 opacity-70 cursor-not-allowed"),
        "data-testid": "interview-score-tile"
      },
        h("div", { className: "flex items-center justify-between" },
          h("div", { className: "w-12 h-12 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow" },
            h(Award, { className: "w-6 h-6" })
          ),
          h(ChevronRight, { className: "w-5 h-5 text-amber-400" })
        ),
        h("h3", { className: "mt-3 font-bold text-slate-900 text-lg" }, "2. Score Entry"),
        h("p", { className: "text-xs text-slate-600 mt-1" }, "Open a shortlisted candidate to capture each panelist's score and remarks on every criterion."),
        h("div", { className: "mt-3 flex items-center gap-3 text-xs" },
          h("span", { className: "px-2 py-0.5 rounded-full bg-white border border-slate-200" }, h("b", null, counts.shortlisted), " shortlisted"),
          h("span", { className: "px-2 py-0.5 rounded-full bg-white border border-slate-200" }, h("b", null, counts.scored), " scored")
        ),
        !ready && h("p", { className: "text-[11px] text-rose-600 mt-2" }, "Complete Configure first.")
      )
    )
  );
}

// ---------------------------------------------------------------------------
// Configure (Criteria + Panelists, tabbed)
// ---------------------------------------------------------------------------
function InterviewConfigure({ sid }) {
  const [tab, setTab] = useState("criteria");
  const [criteria, setCriteria] = useState([]);
  const [panelists, setPanelists] = useState([]);
  const [session, setSession] = useState(null);
  const navigate = useNavigate();

  const reloadCriteria = useCallback(() => {
    axios.get(API + "/sessions/" + sid + "/criteria", { headers: authHeader() })
      .then(r => setCriteria(r.data.items || []));
  }, [sid]);
  const reloadPanelists = useCallback(() => {
    axios.get(API + "/sessions/" + sid + "/panelists", { headers: authHeader() })
      .then(r => setPanelists(r.data.items || []));
  }, [sid]);
  useEffect(() => {
    axios.get(API + "/sessions", { headers: authHeader() })
      .then(r => setSession((r.data.items || []).find(s => s.id === sid)));
    reloadCriteria(); reloadPanelists();
  }, [sid, reloadCriteria, reloadPanelists]);

  return h("div", { className: "space-y-4" },
    h("div", { className: "flex items-center justify-between flex-wrap gap-3" },
      h("div", null,
        h("h1", { className: "text-2xl font-bold text-slate-900 flex items-center gap-2" },
          h(ClipboardCheck, { className: "w-6 h-6 text-indigo-500" }), "Configure Interview — ", session?.name || "…"
        ),
        session?.position_name && h("p", { className: "text-sm text-slate-600 mt-0.5" }, "Position: ", h("b", null, session.position_name))
      ),
      h(Button, { variant: "outline", size: "sm", onClick: () => navigate("/hr/interview/" + sid), "data-testid": "back-to-interview" },
        h(ChevronLeftIcon), " Back to Interview"
      )
    ),
    h("div", { className: "flex items-center gap-2" },
      ["criteria", "panelists"].map(t => h(Button, {
        key: t, variant: tab === t ? "default" : "outline", size: "sm",
        onClick: () => setTab(t),
        className: tab === t ? "bg-slate-900" : "",
        "data-testid": "cfg-tab-" + t
      }, t === "criteria" ? "Criteria Master" : "Panelist Master"))
    ),
    tab === "criteria" && h(CriteriaTab, { sid, criteria, refresh: reloadCriteria }),
    tab === "panelists" && h(PanelistTab, { sid, panelists, refresh: reloadPanelists })
  );
}

const ChevronLeftIcon = () => h("svg", { width: 12, height: 12, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, className: "inline mr-1" },
  h("polyline", { points: "15 18 9 12 15 6" })
);

function CriteriaTab({ sid, criteria, refresh }) {
  const [form, setForm] = useState({ name: "", description: "", min_score: 0, max_score: 10, weight: 1, requires_remarks: false });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!form.name.trim()) { toast.error("Criterion name required"); return; }
    setBusy(true);
    try {
      await axios.post(API + "/sessions/" + sid + "/criteria", form, { headers: authHeader() });
      setForm({ name: "", description: "", min_score: 0, max_score: 10, weight: 1, requires_remarks: false });
      toast.success("Criterion added"); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this criterion? All scores recorded against it will be removed.")) return;
    try { await axios.delete(API + "/criteria/" + id, { headers: authHeader() }); toast.success("Deleted"); refresh(); }
    catch { toast.error("Could not delete"); }
  };
  return h(Card, null, h(CardContent, { className: "p-5 space-y-4" },
    h("p", { className: "text-xs text-slate-600" }, "Define the evaluation criteria the panel will score against."),
    h("div", { className: "grid grid-cols-1 md:grid-cols-6 gap-2 items-end p-3 bg-slate-50 rounded-lg" },
      h("div", { className: "md:col-span-2" }, h(Label, null, "Criterion *"),
        h(Input, { value: form.name, onChange: e => setForm({ ...form, name: e.target.value }), placeholder: "e.g. Technical Knowledge", "data-testid": "crit-name" })
      ),
      h("div", null, h(Label, null, "Min"), h(Input, { type: "number", value: form.min_score, onChange: e => setForm({ ...form, min_score: e.target.value }), "data-testid": "crit-min" })),
      h("div", null, h(Label, null, "Max"), h(Input, { type: "number", value: form.max_score, onChange: e => setForm({ ...form, max_score: e.target.value }), "data-testid": "crit-max" })),
      h("div", null, h(Label, null, "Weight"), h(Input, { type: "number", step: "0.1", value: form.weight, onChange: e => setForm({ ...form, weight: e.target.value }), "data-testid": "crit-weight" })),
      h("div", { className: "flex items-end gap-2" },
        h("label", { className: "flex items-center gap-1 text-xs whitespace-nowrap" },
          h("input", { type: "checkbox", checked: form.requires_remarks, onChange: e => setForm({ ...form, requires_remarks: e.target.checked }), "data-testid": "crit-remarks" }),
          "Req. remarks"
        ),
        h(Button, { onClick: add, disabled: busy, size: "sm", className: "bg-slate-900", "data-testid": "crit-add-btn" }, h(Plus, { className: "w-3 h-3 mr-1" }), "Add")
      )
    ),
    h("table", { className: "w-full text-sm" },
      h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
        h("th", { className: "px-3 py-2" }, "#"), h("th", { className: "px-3 py-2" }, "Criterion"),
        h("th", { className: "px-3 py-2 text-center" }, "Range"), h("th", { className: "px-3 py-2 text-center" }, "Weight"),
        h("th", { className: "px-3 py-2 text-center" }, "Remarks?"), h("th", { className: "px-3 py-2 text-right" }, "")
      )),
      h("tbody", { className: "divide-y divide-slate-100" }, criteria.length === 0
        ? h("tr", null, h("td", { colSpan: 6, className: "text-center text-sm italic text-slate-400 py-8" }, "No criteria yet. Add the first one above."))
        : criteria.map((c, i) => h("tr", { key: c.id, "data-testid": "crit-row-" + c.id },
            h("td", { className: "px-3 py-2 text-slate-500 text-xs" }, i + 1),
            h("td", { className: "px-3 py-2 font-medium" }, c.name),
            h("td", { className: "px-3 py-2 text-center font-mono text-xs" }, c.min_score + " – " + c.max_score),
            h("td", { className: "px-3 py-2 text-center text-xs" }, c.weight),
            h("td", { className: "px-3 py-2 text-center" }, c.requires_remarks ? h(CheckCircle2, { className: "w-4 h-4 text-emerald-500 inline" }) : h("span", { className: "text-slate-300" }, "—")),
            h("td", { className: "px-3 py-2 text-right" },
              h(Button, { variant: "ghost", size: "sm", onClick: () => del(c.id), className: "text-rose-600 hover:text-rose-700", "data-testid": "crit-del-" + c.id }, h(Trash2, { className: "w-3 h-3" }))
            )
          ))
      )
    )
  ));
}

function PanelistTab({ sid, panelists, refresh }) {
  const [form, setForm] = useState({ name: "", email: "", role: "", designation: "" });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!form.name.trim()) { toast.error("Panelist name required"); return; }
    setBusy(true);
    try {
      await axios.post(API + "/sessions/" + sid + "/panelists", form, { headers: authHeader() });
      setForm({ name: "", email: "", role: "", designation: "" });
      toast.success("Panelist added"); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this panelist? All scores recorded by them will be removed.")) return;
    try { await axios.delete(API + "/panelists/" + id, { headers: authHeader() }); toast.success("Deleted"); refresh(); }
    catch { toast.error("Could not delete"); }
  };
  return h(Card, null, h(CardContent, { className: "p-5 space-y-4" },
    h("p", { className: "text-xs text-slate-600" }, "Add the panel of evaluators. Each panelist independently scores every shortlisted candidate against every criterion."),
    h("div", { className: "grid grid-cols-1 md:grid-cols-5 gap-2 items-end p-3 bg-slate-50 rounded-lg" },
      h("div", null, h(Label, null, "Name *"), h(Input, { value: form.name, onChange: e => setForm({ ...form, name: e.target.value }), placeholder: "Dr. Anita Sharma", "data-testid": "panel-name" })),
      h("div", null, h(Label, null, "Email"), h(Input, { value: form.email, onChange: e => setForm({ ...form, email: e.target.value }), placeholder: "anita@iith.ac.in", "data-testid": "panel-email" })),
      h("div", null, h(Label, null, "Role"), h(Input, { value: form.role, onChange: e => setForm({ ...form, role: e.target.value }), placeholder: "Chair / Member", "data-testid": "panel-role" })),
      h("div", null, h(Label, null, "Designation"), h(Input, { value: form.designation, onChange: e => setForm({ ...form, designation: e.target.value }), placeholder: "Professor", "data-testid": "panel-designation" })),
      h("div", { className: "flex justify-end" },
        h(Button, { onClick: add, disabled: busy, size: "sm", className: "bg-slate-900", "data-testid": "panel-add-btn" }, h(Plus, { className: "w-3 h-3 mr-1" }), "Add")
      )
    ),
    h("table", { className: "w-full text-sm" },
      h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
        h("th", { className: "px-3 py-2" }, "#"), h("th", { className: "px-3 py-2" }, "Name"),
        h("th", { className: "px-3 py-2" }, "Email"), h("th", { className: "px-3 py-2" }, "Role"),
        h("th", { className: "px-3 py-2" }, "Designation"), h("th", { className: "px-3 py-2 text-right" }, "")
      )),
      h("tbody", { className: "divide-y divide-slate-100" }, panelists.length === 0
        ? h("tr", null, h("td", { colSpan: 6, className: "text-center text-sm italic text-slate-400 py-8" }, "No panelists yet. Add the first one above."))
        : panelists.map((p, i) => h("tr", { key: p.id, "data-testid": "panel-row-" + p.id },
            h("td", { className: "px-3 py-2 text-slate-500 text-xs" }, i + 1),
            h("td", { className: "px-3 py-2 font-medium" }, p.name),
            h("td", { className: "px-3 py-2 text-xs text-slate-600" }, p.email || "—"),
            h("td", { className: "px-3 py-2 text-xs" }, p.role || "—"),
            h("td", { className: "px-3 py-2 text-xs text-slate-600" }, p.designation || "—"),
            h("td", { className: "px-3 py-2 text-right" },
              h(Button, { variant: "ghost", size: "sm", onClick: () => del(p.id), className: "text-rose-600 hover:text-rose-700", "data-testid": "panel-del-" + p.id }, h(Trash2, { className: "w-3 h-3" }))
            )
          ))
      )
    )
  ));
}

// ---------------------------------------------------------------------------
// Score Entry — shortlisted list
// ---------------------------------------------------------------------------
function InterviewScoreList({ sid }) {
  const [session, setSession] = useState(null);
  const [shortlist, setShortlist] = useState([]);
  const [scoredApps, setScoredApps] = useState(new Set());
  const navigate = useNavigate();
  useEffect(() => {
    axios.get(API + "/sessions", { headers: authHeader() })
      .then(r => setSession((r.data.items || []).find(s => s.id === sid)));
    axios.get(API + "/sessions/" + sid + "/candidates", { headers: authHeader() })
      .then(r => setShortlist((r.data.items || []).filter(c => c.shortlisted)));
    axios.get(API + "/sessions/" + sid + "/summary", { headers: authHeader() })
      .then(r => setScoredApps(new Set((r.data.candidates || []).filter(c => c.interview_total_pct != null).map(c => c.application_no))));
  }, [sid]);
  return h("div", { className: "space-y-4" },
    h("div", { className: "flex items-center justify-between flex-wrap gap-3" },
      h("div", null,
        h("h1", { className: "text-2xl font-bold text-slate-900 flex items-center gap-2" },
          h(Award, { className: "w-6 h-6 text-amber-500" }), "Score Entry — ", session?.name || "…"
        ),
        session?.position_name && h("p", { className: "text-sm text-slate-600 mt-0.5" }, "Position: ", h("b", null, session.position_name)),
        h("p", { className: "text-xs text-slate-500 mt-1" }, "Click a candidate to open the scoring screen. Click the name to view their full profile.")
      ),
      h(Button, { variant: "outline", size: "sm", onClick: () => navigate("/hr/interview/" + sid), "data-testid": "back-to-interview" },
        h(ChevronLeftIcon), " Back"
      )
    ),
    h(Card, null, h(CardContent, { className: "p-4" },
      shortlist.length === 0
        ? h("p", { className: "text-sm text-slate-500 italic text-center py-10" }, "No shortlisted candidates. Go to Shortlist Candidates to mark candidates first.")
        : h("div", { className: "overflow-x-auto" }, h("table", { className: "w-full text-sm" },
            h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
              h("th", { className: "px-3 py-2" }, "Application No."),
              h("th", { className: "px-3 py-2" }, "Name"),
              h("th", { className: "px-3 py-2" }, "Category"),
              h("th", { className: "px-3 py-2 text-center" }, "Exam Score"),
              h("th", { className: "px-3 py-2 text-center" }, "Scoring Status"),
              h("th", { className: "px-3 py-2 text-right" }, "")
            )),
            h("tbody", { className: "divide-y divide-slate-100" }, shortlist.map(c => {
              const scored = scoredApps.has(c.application_no);
              return h("tr", { key: c.application_no, className: "hover:bg-amber-50/30 cursor-pointer", onClick: () => navigate("/hr/interview/" + sid + "/score/" + c.application_no), "data-testid": "scoring-row-" + c.application_no },
                h("td", { className: "px-3 py-3 font-mono text-xs text-amber-700 hover:underline" }, c.application_no),
                h("td", { className: "px-3 py-3 font-semibold text-slate-900 hover:underline" }, c.name),
                h("td", { className: "px-3 py-3 text-xs" }, c.category || "—"),
                h("td", { className: "px-3 py-3 text-center text-xs font-mono" }, c.exam_score != null ? c.exam_score + " / " + (c.exam_max || 100) : "—"),
                h("td", { className: "px-3 py-3 text-center" },
                  scored ? h(Badge, { className: "bg-emerald-100 text-emerald-700 text-[10px]" }, "Scored")
                    : h(Badge, { variant: "outline", className: "text-[10px]" }, "Pending")
                ),
                h("td", { className: "px-3 py-3 text-right" },
                  h(ChevronRight, { className: "w-4 h-4 text-slate-400 inline" })
                )
              );
            }))
          ))
    ))
  );
}

// ---------------------------------------------------------------------------
// Per-candidate scoring page — matrix of panelists × criteria
// ---------------------------------------------------------------------------
function InterviewScorePage({ sid, appNo }) {
  const [session, setSession] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [panelists, setPanelists] = useState([]);
  const [scores, setScores] = useState({}); // { panelist_id: { criteria_id: { score, remarks } } }
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    // Fire each request independently — a single failure (e.g. profile photo
    // 404 on S3) must NOT blank the whole scoring matrix.
    axios.get(API + "/sessions", { headers: authHeader() })
      .then(r => setSession((r.data.items || []).find(x => x.id === sid))).catch(() => {});
    axios.get(API + "/sessions/" + sid + "/criteria", { headers: authHeader() })
      .then(r => setCriteria(r.data.items || [])).catch(() => setCriteria([]));
    const pPanelists = axios.get(API + "/sessions/" + sid + "/panelists", { headers: authHeader() })
      .then(r => { const items = r.data.items || []; setPanelists(items); return items; })
      .catch(() => { setPanelists([]); return []; });
    const pExist = axios.get(API + "/sessions/" + sid + "/interview-scores/" + appNo, { headers: authHeader() })
      .then(r => r.data.items || []).catch(() => []);
    axios.get(API + "/sessions/" + sid + "/candidates/" + appNo + "/profile", { headers: authHeader() })
      .then(r => setProfile(r.data)).catch(() => setProfile(null));
    // Hydrate scores once panelists + existing scores are both available
    Promise.all([pPanelists, pExist]).then(([pList, existing]) => {
      const sc = {};
      existing.forEach(item => {
        const pid = item.panelist_id || (pList.find(x => x.email && x.email === item.panelist_email)?.id);
        if (!pid) return;
        if (!sc[pid]) sc[pid] = {};
        sc[pid][item.criteria_id] = { score: item.score, remarks: item.remarks || "" };
      });
      setScores(sc);
      setLoading(false);
    });
  }, [sid, appNo]);
  useEffect(() => { load(); }, [load]);

  const setCell = (pid, cid, field, val) => {
    setScores(prev => ({
      ...prev,
      [pid]: { ...(prev[pid] || {}), [cid]: { ...(prev[pid]?.[cid] || {}), [field]: val } }
    }));
  };

  const save = async () => {
    // Build entries
    const entries = [];
    for (const p of panelists) {
      for (const c of criteria) {
        const cell = scores[p.id]?.[c.id];
        if (cell?.score === undefined || cell?.score === null || cell?.score === "") continue;
        // Required remarks check
        if (c.requires_remarks && !cell.remarks?.trim()) {
          toast.error(`Remarks required for "${c.name}" from ${p.name}`);
          return;
        }
        // Range check
        const sv = Number(cell.score);
        if (sv < (c.min_score ?? 0) || sv > (c.max_score ?? 100)) {
          toast.error(`Score for "${c.name}" from ${p.name} must be between ${c.min_score} and ${c.max_score}`);
          return;
        }
        entries.push({ panelist_id: p.id, criteria_id: c.id, score: cell.score, remarks: cell.remarks || "" });
      }
    }
    if (entries.length === 0) { toast.error("Enter at least one score"); return; }
    setBusy(true);
    try {
      const r = await axios.post(API + "/sessions/" + sid + "/interview-scores/bulk",
        { application_no: appNo, entries }, { headers: authHeader() });
      toast.success(r.data.saved_count + " score cells saved");
      navigate("/hr/interview/" + sid + "/score");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading) return h(Card, null, h(CardContent, { className: "p-8 text-center text-slate-500" }, "Loading\u2026"));
  if (criteria.length === 0) return h(Card, null, h(CardContent, { className: "p-8 text-center text-slate-500" }, "No criteria defined. ", h(Link, { to: "/hr/interview/" + sid + "/configure", className: "text-indigo-600 underline" }, "Go to Configure"), "."));
  if (panelists.length === 0) return h(Card, null, h(CardContent, { className: "p-8 text-center text-slate-500" }, "No panelists defined. ", h(Link, { to: "/hr/interview/" + sid + "/configure", className: "text-indigo-600 underline" }, "Go to Configure"), "."));

  return h("div", { className: "space-y-4" },
    // Header with candidate identity
    h("div", { className: "flex items-start justify-between flex-wrap gap-3" },
      h("div", null,
        h("p", { className: "text-xs text-slate-500" }, (session?.name || "") + (session?.position_name ? " \u00B7 " + session.position_name : "")),
        h("h1", { className: "text-2xl font-bold text-slate-900 flex items-center gap-2 mt-0.5" },
          h("button", { onClick: () => setProfileOpen(true), className: "hover:underline cursor-pointer text-slate-900", "data-testid": "open-profile-btn" },
            profile?.name || "…"
          ),
          h("span", { className: "text-sm font-mono text-slate-400" }, "(" + (appNo) + ")")
        ),
        h(Button, { variant: "outline", size: "sm", onClick: () => setProfileOpen(true), className: "mt-2", "data-testid": "view-profile-btn" }, "View Full Profile")
      ),
      h("div", { className: "flex items-center gap-2" },
        h(Button, { variant: "outline", size: "sm", onClick: () => navigate("/hr/interview/" + sid + "/score"), "data-testid": "back-to-list" },
          h(ChevronLeftIcon), " Back to list"
        ),
        h(Button, { onClick: save, disabled: busy, className: "bg-indigo-600 hover:bg-indigo-700", "data-testid": "save-all-scores-btn" }, busy ? "Saving…" : "Save All Scores")
      )
    ),

    // Score Matrix — one accordion per panelist with rows per criterion
    h("div", { className: "space-y-4" }, panelists.map(p =>
      h(Card, { key: p.id, className: "border-l-4 border-l-indigo-500" }, h(CardContent, { className: "p-4" },
        h("div", { className: "flex items-center justify-between mb-3 pb-2 border-b" },
          h("div", null,
            h("p", { className: "font-bold text-slate-900" }, p.name),
            h("p", { className: "text-xs text-slate-500" }, [p.role, p.designation, p.email].filter(Boolean).join(" · "))
          ),
          h(Badge, { className: "bg-indigo-100 text-indigo-700 text-[10px]" }, "Panelist")
        ),
        h("div", { className: "space-y-2" }, criteria.map(c => {
          const cell = scores[p.id]?.[c.id] || {};
          return h("div", { key: c.id, className: "grid grid-cols-1 md:grid-cols-12 gap-2 items-center p-2 rounded hover:bg-slate-50" },
            h("div", { className: "md:col-span-4" },
              h("p", { className: "text-sm font-medium" }, c.name,
                c.requires_remarks && h("span", { className: "ml-1 text-xs text-rose-500" }, "*")
              ),
              h("p", { className: "text-[10px] text-slate-500 font-mono" }, c.min_score + " – " + c.max_score + " · weight " + c.weight)
            ),
            h("div", { className: "md:col-span-2" },
              h(Input, {
                type: "number", step: "0.01", min: c.min_score, max: c.max_score,
                placeholder: "Score", value: cell.score ?? "",
                onChange: e => setCell(p.id, c.id, "score", e.target.value),
                "data-testid": "score-" + p.id + "-" + c.id,
                className: "text-center"
              })
            ),
            h("div", { className: "md:col-span-6" },
              h(Input, {
                placeholder: c.requires_remarks ? "Remarks (required)" : "Remarks (optional)",
                value: cell.remarks ?? "",
                onChange: e => setCell(p.id, c.id, "remarks", e.target.value),
                "data-testid": "remarks-" + p.id + "-" + c.id
              })
            )
          );
        }))
      ))
    )),

    // Profile dialog
    h(CandidateProfileDialog, { open: profileOpen, onClose: () => setProfileOpen(false), profile })
  );
}

function CandidateProfileDialog({ open, onClose, profile }) {
  if (!profile) return null;
  const initials = (profile.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  return h(Dialog, { open, onOpenChange: onClose },
    h(DialogContent, { className: "max-w-3xl max-h-[85vh] overflow-y-auto", "data-testid": "candidate-profile-dialog" },
      h(DialogHeader, null, h(DialogTitle, null, "Candidate Profile")),
      h("div", { className: "space-y-4" },
        // Header block
        h("div", { className: "flex gap-4 p-4 bg-slate-50 rounded-lg" },
          h("div", { className: "w-24 h-28 rounded-lg overflow-hidden border-2 border-white shadow bg-slate-200 flex items-center justify-center shrink-0" },
            profile.photo_url
              ? h("img", { src: profile.photo_url, alt: profile.name, className: "w-full h-full object-cover", onError: e => { e.currentTarget.style.display = "none"; } })
              : h("span", { className: "text-2xl font-bold text-slate-400" }, initials)
          ),
          h("div", { className: "flex-1 min-w-0" },
            h("p", { className: "text-xs font-mono text-slate-500" }, profile.application_no),
            h("h2", { className: "text-xl font-bold text-slate-900" }, profile.name),
            h("div", { className: "flex flex-wrap gap-2 mt-2 text-xs" },
              profile.category && h(Badge, { variant: "outline", className: "border-emerald-300 text-emerald-700" }, profile.category),
              profile.total_experience && h(Badge, { variant: "outline", className: "border-blue-300 text-blue-700" }, "Experience: " + profile.total_experience)
            ),
            h("div", { className: "mt-2 space-y-1 text-xs text-slate-600" },
              profile.email && h("div", null, h(Mail, { className: "w-3 h-3 inline mr-1" }), profile.email),
              profile.mobile && h("div", null, h(Phone, { className: "w-3 h-3 inline mr-1" }), profile.mobile)
            ),
            profile.exam && h("div", { className: "mt-2 p-2 rounded bg-white border border-amber-200" },
              h("p", { className: "text-[10px] uppercase tracking-wider text-amber-700 font-semibold" }, "Exam Score"),
              h("p", { className: "text-sm font-mono mt-0.5" }, (profile.exam.exam_score ?? "—") + " / " + (profile.exam.exam_max ?? 100)),
              profile.exam.exam_remarks && h("p", { className: "text-xs text-slate-600 mt-1" }, profile.exam.exam_remarks)
            )
          )
        ),
        // Education
        (profile.education || []).length > 0 && h("div", null,
          h("h3", { className: "text-sm font-bold text-slate-700 flex items-center gap-1 mb-2" }, h(GraduationCap, { className: "w-4 h-4 text-amber-500" }), "Education (" + profile.education.length + ")"),
          h("div", { className: "space-y-1.5" }, profile.education.map((e, i) => h("div", { key: i, className: "p-2 bg-white border rounded text-xs" },
            h("p", { className: "font-semibold" }, e.exam || e.edu_level),
            h("p", { className: "text-slate-500" }, [e.edu_level !== (e.exam || e.edu_level) ? e.edu_level : null, e.specialization, e.year_passing ? "Year " + e.year_passing : null, e.pct ? e.pct + "%" : null].filter(Boolean).join(" · "))
          )))
        ),
        // Experience
        (profile.experience || []).length > 0 && h("div", null,
          h("h3", { className: "text-sm font-bold text-slate-700 flex items-center gap-1 mb-2" }, h(Briefcase, { className: "w-4 h-4 text-blue-500" }), "Experience (" + profile.experience.length + ")"),
          h("div", { className: "space-y-1.5" }, profile.experience.map((e, i) => h("div", { key: i, className: "p-2 bg-white border rounded text-xs" },
            h("p", { className: "font-semibold" }, e.post),
            h("p", { className: "text-slate-500" }, e.organization),
            (e.from || e.to || e.service_duration) && h("p", { className: "text-slate-400 text-[10px]" }, [e.from, e.to].filter(Boolean).join(" → ") || e.service_duration)
          )))
        ),
        // Achievements
        (profile.achievements || []).length > 0 && h("div", null,
          h("h3", { className: "text-sm font-bold text-slate-700 flex items-center gap-1 mb-2" }, h(Award, { className: "w-4 h-4 text-rose-500" }), "Achievements (" + profile.achievements.length + ")"),
          h("div", { className: "space-y-1" }, profile.achievements.map((a, i) => h("div", { key: i, className: "p-2 bg-white border rounded text-xs" },
            h("p", { className: "font-medium" }, a.title), a.authority && h("p", { className: "text-slate-500 text-[11px]" }, a.authority)
          )))
        )
      ),
      h(DialogFooter, null, h(Button, { variant: "outline", onClick: onClose }, "Close"))
    )
  );
}


// ============================================================================
// Final Report — ranking, decision, PDF downloads
// ============================================================================
export function HRReport() {
  const sid = window.location.pathname.split("/hr/report/")[1];
  if (!sid) return h(HRSessionPicker, { basePath: "/hr/report", title: "Final Report", subtitle: "Pick a session to view ranking and generate PDF reports.", icon: FileDown });
  return h(HRReportDetail, { sid });
}

function HRReportDetail({ sid }) {
  const [data, setData] = useState(null);
  const [editingFinal, setEditingFinal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(API + "/sessions/" + sid + "/summary", { headers: authHeader() })
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load summary"));
  }, [sid]);
  useEffect(load, [load]);

  const saveFinal = async () => {
    setBusy(true);
    try {
      await axios.put(API + "/sessions/" + sid + "/final-status/" + editingFinal.application_no, {
        final_status: editingFinal.final_status,
        final_remarks: editingFinal.final_remarks,
      }, { headers: authHeader() });
      toast.success("Decision saved");
      setEditingFinal(null); load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (path, filename) => {
    try {
      const r = await axios.get(API + path, { headers: authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF generation failed"); }
  };

  if (!data) return h("p", { className: "text-sm text-slate-500" }, "Loading…");
  const s = data.session;

  return h("div", { className: "space-y-4" },
    h("div", { className: "flex items-start justify-between flex-wrap gap-3" },
      h("div", null,
        h("h1", { className: "text-2xl font-bold text-slate-900 flex items-center gap-2" },
          h(Award, { className: "w-6 h-6 text-rose-500" }), "Final Report — ", s.name
        ),
        s.position_name && h("p", { className: "text-sm text-slate-600 mt-0.5" }, "Position: ", h("b", null, s.position_name))
      ),
      h(Button, {
        onClick: () => downloadPdf("/sessions/" + sid + "/report/comparative.pdf", "ranking_" + s.name + ".pdf"),
        className: "bg-rose-600 hover:bg-rose-700", "data-testid": "download-comparative-pdf"
      }, h(FileDown, { className: "w-4 h-4 mr-1" }), "Comparative Ranking PDF")
    ),

    h("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-3" },
      [
        { label: "Roster", value: data.roster_count },
        { label: "Shortlisted", value: data.shortlisted_count },
        { label: "Interviewed", value: data.interviewed_count },
        { label: "Criteria", value: data.criteria.length },
      ].map((t, i) => h("div", { key: i, className: "p-3 rounded-xl border bg-white" },
        h("p", { className: "text-[10px] uppercase tracking-wider text-slate-500 font-semibold" }, t.label),
        h("p", { className: "text-2xl font-bold text-slate-900 mt-0.5" }, t.value)
      ))
    ),

    h(Card, null, h(CardContent, { className: "p-4 overflow-x-auto" },
      h("table", { className: "w-full text-sm" },
        h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
          h("th", { className: "px-2 py-2" }, "Rank"),
          h("th", { className: "px-2 py-2" }, "App. No."),
          h("th", { className: "px-2 py-2" }, "Name"),
          data.criteria.map(c => h("th", { key: c.id, className: "px-2 py-2 text-center text-[10px]" }, c.name.slice(0, 14))),
          h("th", { className: "px-2 py-2 text-center" }, "Exam %"),
          h("th", { className: "px-2 py-2 text-center" }, "Interview %"),
          h("th", { className: "px-2 py-2 text-center" }, "Decision"),
          h("th", { className: "px-2 py-2 text-right" }, "")
        )),
        h("tbody", { className: "divide-y divide-slate-100" }, data.candidates
          .filter(c => c.shortlisted || c.interview_total_pct != null || c.attended)
          .map(c => {
            const exam_pct = c.exam_score != null && c.exam_max > 0 ? Math.round((c.exam_score / c.exam_max) * 1000) / 10 : null;
            const cmap = {}; (c.criteria_breakdown || []).forEach(cb => cmap[cb.criteria_id] = cb);
            const status = c.final_status;
            return h("tr", { key: c.application_no, className: "hover:bg-slate-50", "data-testid": "rank-row-" + c.application_no },
              h("td", { className: "px-2 py-2 text-center font-bold" }, c.rank || "—"),
              h("td", { className: "px-2 py-2 font-mono text-xs" }, c.application_no),
              h("td", { className: "px-2 py-2 font-medium" }, c.name),
              data.criteria.map(cr => h("td", { key: cr.id, className: "px-2 py-2 text-center text-xs font-mono" },
                cmap[cr.id]?.avg_score != null ? cmap[cr.id].avg_score : "—"
              )),
              h("td", { className: "px-2 py-2 text-center text-xs" }, exam_pct != null ? exam_pct + "%" : "—"),
              h("td", { className: "px-2 py-2 text-center text-xs font-bold" }, c.interview_total_pct != null ? c.interview_total_pct + "%" : "—"),
              h("td", { className: "px-2 py-2 text-center" },
                status === "SELECTED" ? h(Badge, { className: "bg-emerald-100 text-emerald-700 text-[10px]" }, "Selected")
                  : status === "REJECTED" ? h(Badge, { className: "bg-rose-100 text-rose-700 text-[10px]" }, "Rejected")
                  : status === "WAITLIST" ? h(Badge, { className: "bg-amber-100 text-amber-700 text-[10px]" }, "Waitlist")
                  : h("span", { className: "text-slate-400 italic text-xs" }, "Pending")
              ),
              h("td", { className: "px-2 py-2 text-right" },
                h("div", { className: "flex items-center gap-1 justify-end" },
                  h(Button, {
                    size: "sm", variant: "ghost",
                    onClick: () => setEditingFinal({ application_no: c.application_no, name: c.name, final_status: status || "", final_remarks: c.final_remarks || "" }),
                    "data-testid": "decide-" + c.application_no,
                    className: "text-xs"
                  }, "Decide"),
                  h(Button, {
                    size: "sm", variant: "ghost",
                    onClick: () => downloadPdf("/sessions/" + sid + "/report/" + c.application_no + ".pdf", "candidate_" + c.application_no + ".pdf"),
                    "data-testid": "pdf-" + c.application_no,
                    className: "text-xs text-rose-600"
                  }, h(FileDown, { className: "w-3 h-3" }))
                )
              )
            );
          })
        )
      )
    )),

    editingFinal && h(Dialog, { open: true, onOpenChange: () => setEditingFinal(null) },
      h(DialogContent, null,
        h(DialogHeader, null, h(DialogTitle, null, "Final Decision — " + editingFinal.name)),
        h("div", { className: "space-y-3" },
          h("div", { className: "flex gap-2" },
            ["SELECTED", "WAITLIST", "REJECTED"].map(opt => h(Button, {
              key: opt,
              variant: editingFinal.final_status === opt ? "default" : "outline",
              onClick: () => setEditingFinal({ ...editingFinal, final_status: opt }),
              "data-testid": "final-" + opt,
              className: editingFinal.final_status === opt
                ? (opt === "SELECTED" ? "bg-emerald-600" : opt === "REJECTED" ? "bg-rose-600" : "bg-amber-500")
                : ""
            }, opt))
          ),
          h(Textarea, {
            rows: 3, placeholder: "Remarks…",
            value: editingFinal.final_remarks,
            onChange: e => setEditingFinal({ ...editingFinal, final_remarks: e.target.value }),
            "data-testid": "final-remarks"
          })
        ),
        h(DialogFooter, { className: "mt-3" },
          h(Button, { variant: "outline", onClick: () => setEditingFinal(null) }, "Cancel"),
          h(Button, { onClick: saveFinal, disabled: busy, className: "bg-slate-900", "data-testid": "final-save-btn" }, "Save Decision")
        )
      )
    )
  );
}
